import React from 'react';
import { DEPENDENCY_WEIGHTS, getZoneForScore } from '../../utils/algorithms';
import RadarChart from './RadarChart';
import DiagnosticHeader from './DiagnosticHeader';
import { getCompetenciesForDependency } from '../../utils/umCompetencies';

function ReportPage({ formData, calculations, topInsights, onStartOver }) {
  
  return (
  <>
    <DiagnosticHeader showNav={true} />

    {/* Print-only header */}
    <div className="hidden print:block mb-2">
    <div className="flex items-center justify-between border-b-2 border-gray-200 pb-1 mb-2">
        <img 
        src={process.env.PUBLIC_URL + '/df-web-logo.svg'} 
        alt="Daring Futures" 
        className="h-16" 
        />
        <div className="text-right">
        <p className="text-[#697A90] text-sm">Hidden Work Diagnostic Report</p>
        <p className="text-[#697A90] text-xs">{new Date().toLocaleDateString()}</p>
        </div>
    </div>
    </div>

    <div className="max-w-5xl mx-auto p-8 pt-28 print:pt-0">
      {/* Header */}
      <div className="bg-white rounded-lg p-10 mb-8 border border-gray-200 text-center">
        <h1 className="text-4xl font-bold text-[#152C47] mb-3 font-['Poppins']">
          Your Hidden Work Diagnostic Results
        </h1>
        <p className="text-[#697A90]">
          {formData.firstName} {formData.lastName} • {formData.role}
        </p>
      </div>

    {/* Dependency Scores */}
    <div id="dependency-scores" className="bg-white rounded-lg p-8 mb-8 border border-gray-200">
    <h2 className="text-3xl font-bold text-[#152C47] mb-5 pb-3 border-b-2 border-gray-200 font-['Poppins']">
        Your Dependency Scores
    </h2>

    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-sm">
    <p className="font-semibold text-gray-800 mb-2">Interpretation Guide:</p>
    <div className="space-y-2 text-gray-600">
        <p>
        <span className="font-semibold text-red-600">0–4 = Structural constraint</span>
        <br />
        <span className="text-xs">Value leakage likely. Recoverability suppressed. This area needs focused intervention early.</span>
        </p>
        <p>
        <span className="font-semibold text-amber-600">5–7 = Capable but inconsistent</span>
        <br />
        <span className="text-xs">Can support recoverability, but becomes fragile under pressure. Needs reinforcement.</span>
        </p>
        <p>
        <span className="font-semibold text-green-600">8–10 = Enabler</span>
        <br />
        <span className="text-xs">Increases recoverability. Prioritise maintaining this as an organisational strength.</span>
        </p>
    </div>
    </div>

    {DEPENDENCY_WEIGHTS.map((dep) => {
        const score = formData[`score${dep.id}`];
        
        return (
        <div key={dep.id} className="mb-6">
            <div className="flex justify-between items-center mb-2">
            <span className="font-semibold text-gray-800 text-sm">
                {dep.id}. {dep.name}
            </span>
            <span className="text-[#697A90] text-sm">Score: {score}</span>
            </div>
            
            {/* Zone bar */}
            <div className="relative h-10 bg-gray-100 rounded-lg overflow-visible flex mx-1">
            {/* Compounded zone (0-4) */}
            <div className="bg-red-100 flex-[4] flex items-center justify-center text-xs text-red-800">
                0-4
            </div>
            {/* Clouded zone (5-7) */}
            <div className="bg-amber-100 flex-[3] flex items-center justify-center text-xs text-amber-800">
                5-7
            </div>
            {/* Clear zone (8-10) */}
            <div className="bg-green-100 flex-[3] flex items-center justify-center text-xs text-green-800">
                8-10
            </div>
            
            {/* Score marker */}
            <div className="absolute top-0 h-full w-1.5 bg-[#152C47] shadow-lg transform -translate-x-1/2"
                style={{ left: `${(score / 10) * 100}%` }}
                />

                </div>
            </div>
            );
        })}
        </div>

    {/* Radar Chart */}
    <div id="radar-chart" className="bg-white rounded-lg p-8 mb-8 border border-gray-200">
    <h2 className="text-3xl font-bold text-[#152C47] mb-5 pb-3 border-b-2 border-gray-200 font-['Poppins']">
        Recoverability Radar
    </h2>
    <p className="text-[#697A90] text-sm mb-6">
        Visual comparison of your current state versus target state across all 14 dependencies.
    </p>
    <RadarChart formData={formData} />
    </div>

    {/* Key Insights */}
    <div id="key-insights" className="bg-white rounded-lg p-8 mb-8 border border-gray-200">
    <h2 className="text-3xl font-bold text-[#152C47] mb-5 pb-3 border-b-2 border-gray-200 font-['Poppins']">
        Key Insights
    </h2>

    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-sm">
    <p className="font-semibold text-gray-800 mb-2">Zone Definitions:</p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-gray-600 text-xs">
        <div>
        <span className="font-semibold text-red-600">COMPOUNDED</span>
        <p>Structural constraint requiring focused intervention</p>
        </div>
        <div>
        <span className="font-semibold text-amber-600">CLOUDED</span>
        <p>Capable but inconsistent, needs reinforcement</p>
        </div>
        <div>
        <span className="font-semibold text-green-600">CLEAR</span>
        <p>Enabler that increases recoverability</p>
        </div>
    </div>
    </div>

    <p className="text-[#697A90] text-sm mb-6">
        These are your three highest-impact areas for improvement based on weighted scores.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
    {topInsights.map((insight, index) => {
        const zone = getZoneForScore(insight.score);
        const zoneColors = {
        compounded: 'border-red-500 bg-red-50',
        clouded: 'border-amber-500 bg-amber-50',
        clear: 'border-green-500 bg-green-50'
        };
        const badgeColors = {
        compounded: 'bg-red-500',
        clouded: 'bg-amber-500',
        clear: 'bg-green-500'
        };
        
        const competencies = getCompetenciesForDependency(insight.id);
        
        return (
        <div key={insight.id} className={`border-l-4 rounded-lg p-6 ${zoneColors[zone]}`}>
            <div className="flex items-start justify-between mb-3">
            <span className={`${badgeColors[zone]} text-white text-xs font-bold px-3 py-1 rounded uppercase`}>
                {zone}
            </span>
            <span className="text-gray-600 text-sm">Score: {insight.score}</span>
            </div>
            <h3 className="font-bold text-gray-800 mb-2">
            {insight.id}. {insight.name}
            </h3>
            <p className="text-gray-600 text-sm mb-3">
            Weighted impact: {(insight.weightedImpact / 100).toFixed(2)}
            </p>
            
            {/* UM Competencies */}
            {competencies.length > 0 && (
            <div className="border-t border-gray-300 pt-3 mt-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">
                Recommended Focus:
                </p>
                <ul className="space-y-1">
                {competencies.map((comp, idx) => (
                    <li key={idx} className="text-xs">
                    <a 
                        href={comp.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#152C47] hover:text-[#86C2CA] hover:underline transition-colors"
                    >
                        → {comp.name}
                    </a>
                    </li>
                ))}
                </ul>
            </div>
            )}
        </div>
        );
    })}
    </div>    

    </div>

      {/* Bottom Line Metrics */}
      <div id="bottom-line" className="bg-white rounded-lg p-8 mb-8 border border-gray-200">
        <h2 className="text-3xl font-bold text-[#152C47] mb-5 pb-3 border-b-2 border-gray-200 font-['Poppins']">
          The Bottom Line
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Metric 1: Value at Stake */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-center">
            <p className="text-[#697A90] text-sm mb-2">Value at Stake</p>
            <p className="text-4xl font-bold text-[#86C2CA] font-['Poppins']">
              £{calculations.valueAtStake.toFixed(2)}M
            </p>
          </div>

          {/* Metric 2: Value Lost */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-center">
            <p className="text-[#697A90] text-sm mb-2">Value Lost (43%)</p>
            <p className="text-4xl font-bold text-[#86C2CA] font-['Poppins']">
              £{calculations.valueLost.toFixed(2)}M
            </p>
          </div>

          {/* Metric 3: Theoretically Recoverable */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-center">
            <p className="text-[#697A90] text-sm mb-2">Theoretically Recoverable</p>
            <p className="text-4xl font-bold text-[#86C2CA] font-['Poppins']">
              £{calculations.valueTheoreticallyRecoverable.toFixed(2)}M
            </p>
          </div>

          {/* Metric 4: Current Capability */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-center">
            <p className="text-[#697A90] text-sm mb-2">Current Capability Recoverable</p>
            <p className="text-4xl font-bold text-[#86C2CA] font-['Poppins']">
              £{calculations.valueCurrentCapabilityRecoverable.toFixed(2)}M
            </p>
          </div>

          {/* Metric 5: Deficit - HIGHLIGHTED */}
          <div className="bg-[#86C2CA] bg-opacity-10 p-6 rounded-lg border-2 border-[#86C2CA] text-center md:col-span-2 lg:col-span-1">
            <p className="text-[#697A90] text-sm mb-2 font-semibold">The Deficit</p>
            <p className="text-5xl font-bold text-[#152C47] font-['Poppins']">
              £{calculations.valueDeficit.toFixed(2)}M
            </p>
          </div>
        </div>
      </div>

    {/* Call to Action */}
    <div className="bg-gradient-to-br from-[#152C47] to-[#1e3a5f] rounded-lg p-10 mb-8 border-2 border-[#86C2CA] text-center">
    <h2 className="text-3xl font-bold text-[#86C2CA] mb-4 font-['Poppins']">
        Ready to Recover That Value?
    </h2>
    <p className="text-gray-200 text-lg mb-6 max-w-2xl mx-auto leading-relaxed">
        Your diagnostic reveals £{calculations.valueDeficit.toFixed(2)}M in recoverable value currently left on the table. 
        The Truth Session is where we surface what's really happening—and start closing that gap.
    </p>
    
        <a href="https://calendly.com/andrewkidd/truth-session"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block bg-[#86C2CA] hover:bg-[#a0d9e0] text-[#152C47] font-bold py-4 px-10 rounded-lg font-['Poppins'] transition-all text-lg">
        Book Your Truth Session →
        </a>
    </div>

    {/* Start Over Button */}
      <div className="text-center mb-8">
        <button
          onClick={onStartOver}
          className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-8 rounded-lg font-['Poppins'] transition-all"
        >
          Start Over
        </button>
      </div>

      {/* Footer */}
      <div className="border-t border-[#B3C9D3] pt-8 text-center text-sm text-[#697A90]">
        <p>© 2025 Daring Futures Ltd | We surface and resolve hidden work</p>
        <p className="mt-2">
          <a href="https://wiki.daringfutures.com/Working+With+Us/Service+Delivery+Policies/Policy+-+Terms+and+Conditions" className="hover:text-[#86C2CA] transition-colors ml-2">Terms & Conditions</a> |
          <a href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Privacy+and+Cookies" className="hover:text-[#86C2CA] transition-colors"> Privacy & Cookie Policy</a> |
          <a href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Licensing+and+Fair+Use" className="hover:text-[#86C2CA] transition-colors ml-2">Fair Use Policy</a> |
          <a href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Anti-Slavery+Statement" className="hover:text-[#86C2CA] transition-colors ml-2">Anti-Slavery Statement</a>
        </p>
      </div>
    </div>
    </>
  );
}

export default ReportPage;